import { omitBy } from "lodash";
import type {
  BenefitsPayload,
  CategoryScores,
  CategoryScoresPayload,
  Pillar,
  PillarPayload,
  Pillars,
  PillarsPayload,
  SatelliteImage,
  SolutionPayload,
} from "../types";
import type { SatelliteImageFormValue } from "./types";
import { sanitiseText } from "../formatting/sanitise-rich-text";
import {
  normaliseInterventions,
  normaliseOutcomes,
} from "../formatting/domain-value-manipulation";

const isEmptyPayloadValue = (field: unknown) =>
  field === null ||
  field === undefined ||
  field === "" ||
  (typeof field === "object" && Object.keys(field).length === 0);

export const removeNonValues = (payload: SolutionPayload) => {
  const date = omitBy(payload.date, (field) => !field);
  const coordinates =
    payload.coordinates?.latitude && payload.coordinates?.longitude
      ? payload.coordinates
      : null;
  const interventions = normaliseInterventions(payload);
  const outcomes = normaliseOutcomes(payload);
  const programmeInfo = Object.fromEntries(
    Object.entries(payload.programmeInfo || {}).filter(
      ([, value]) =>
        (typeof value === "string" && value) ||
        (Array.isArray(value) && value.length),
    ),
  );
  const internalUseOnly = Object.fromEntries(
    Object.entries(payload.internalUseOnly || {}).filter(([, value]) => value),
  );
  const nestedValues = {
    ...payload,
    coordinates,
    date,
    programmeInfo,
    internalUseOnly,
  };
  const topLevelValues = omitBy(nestedValues, isEmptyPayloadValue);

  return { ...topLevelValues, interventions, outcomes };
};

export const normaliseUniqueBenefits = (
  benefits: ReadonlyArray<string>,
): Array<string> => benefits.map((benefit) => benefit.trim()).filter(Boolean);

export function countWords(text: string): number {
  const normalisedText = text.replace(/\u00a0/g, " ").trim();
  if (!normalisedText) return 0;
  return normalisedText.split(/\s+/u).length;
}

export function countHtmlTextWords(html: string): number {
  const sanitisedHtml = sanitiseText(html);
  if (!sanitisedHtml) return 0;

  const document = new DOMParser().parseFromString(sanitisedHtml, "text/html");
  document.body
    .querySelectorAll("br")
    .forEach((element) => element.replaceWith(" "));
  document.body
    .querySelectorAll("p, li")
    .forEach((element) => element.after(" "));

  return countWords(document.body.textContent ?? "");
}

const normaliseCategoryScore = (
  category: CategoryScores,
): CategoryScores | undefined => {
  const explanation = category.explanation?.trim();
  const score = category.score;
  const status = category.status ?? undefined;

  if (score === undefined && !explanation && !status) return undefined;

  return {
    ...(score !== undefined ? { score } : {}),
    ...(explanation ? { explanation } : {}),
    ...(status ? { status } : {}),
  };
};

const normaliseScores = (
  scores: Pillar["scores"],
): Pillar["scores"] | undefined => {
  if (!scores) return undefined;

  const normalisedEntries = Object.entries(scores).flatMap(([key, value]) => {
    if (key === "total") {
      return value !== undefined && value !== null ? [[key, value]] : [];
    }

    if (!value || typeof value !== "object") return [];

    const category = normaliseCategoryScore(value as CategoryScores);
    return category ? [[key, category]] : [];
  });

  return normalisedEntries.length
    ? (Object.fromEntries(normalisedEntries) as Pillar["scores"])
    : undefined;
};

const normaliseBenefits = (
  benefits: Pillar["benefits"],
): Pillar["benefits"] | undefined => {
  if (!benefits) return undefined;

  const quantity = benefits.quantity?.trim();
  const description = benefits.description?.trim();

  if (!quantity && !description) return undefined;

  return {
    ...(quantity ? { quantity } : {}),
    ...(description ? { description } : {}),
  };
};

const normalisePillar = (pillar: Pillar | undefined): Pillar | undefined => {
  if (!pillar) return undefined;

  const scores = normaliseScores(pillar.scores);
  const benefits = normaliseBenefits(pillar.benefits);

  if (!scores && !benefits) return undefined;

  return {
    ...(scores ? { scores } : {}),
    ...(benefits ? { benefits } : {}),
  };
};

export const normalisePillars = (
  pillars: Pillars | undefined,
): Pillars | undefined => {
  if (!pillars) return undefined;

  const carbon = normalisePillar(pillars.carbon);
  const biodiversity = normalisePillar(pillars.biodiversity);
  const people = normalisePillar(pillars.people);

  if (!carbon && !biodiversity && !people) return undefined;

  return {
    ...(carbon ? { carbon } : {}),
    ...(biodiversity ? { biodiversity } : {}),
    ...(people ? { people } : {}),
  };
};

export const normaliseCategoryScoreForUpdate = (
  category: CategoryScores,
): CategoryScoresPayload => ({
  score: category.score ?? null,
  explanation: category.explanation?.trim() || null,
  status: category.status ?? null,
});

const normaliseScoresForUpdate = (
  scores: Pillar["scores"],
): PillarPayload["scores"] | undefined => {
  if (!scores) return undefined;

  const normalisedEntries: Array<
    [string, number | CategoryScoresPayload | null | undefined]
  > = Object.entries(scores).map(([key, value]) => {
    if (key === "total") return [key, value ?? null];

    return [
      key,
      value && typeof value === "object"
        ? normaliseCategoryScoreForUpdate(value as CategoryScores)
        : value,
    ];
  });

  if (!Object.prototype.hasOwnProperty.call(scores, "total")) {
    normalisedEntries.unshift(["total", null]);
  }

  return Object.fromEntries(normalisedEntries) as PillarPayload["scores"];
};

export const normaliseBenefitsForUpdate = (
  benefits: Pillar["benefits"],
): BenefitsPayload | undefined => {
  if (!benefits) return undefined;

  const quantity = benefits.quantity?.trim();
  const description = benefits.description?.trim();
  return {
    quantity: quantity || null,
    description: description || null,
  };
};

const normalisePillarForUpdate = (
  pillar: Pillar | undefined,
): PillarPayload | undefined => {
  if (!pillar) return undefined;

  const scores = normaliseScoresForUpdate(pillar.scores);
  const benefits = normaliseBenefitsForUpdate(pillar.benefits);

  if (!scores && !benefits) return undefined;

  return {
    ...(scores ? { scores } : {}),
    ...(benefits ? { benefits } : {}),
  };
};

export const normaliseSatelliteImageryForUpdate = (
  satelliteImagery: Array<SatelliteImageFormValue>,
): Array<SatelliteImage> =>
  satelliteImagery
    .filter(
      (satelliteImage) => satelliteImage.mediaLink || satelliteImage.caption,
    )
    .map(({ mediaLink, caption, source }) => ({
      mediaLink,
      caption,
      ...(source?.trim() ? { source: source.trim() } : {}),
    }));

export const normalisePillarsForUpdate = (
  pillars: Pillars | undefined,
): PillarsPayload | undefined => {
  if (!pillars) return undefined;

  const carbon = normalisePillarForUpdate(pillars.carbon);
  const biodiversity = normalisePillarForUpdate(pillars.biodiversity);
  const people = normalisePillarForUpdate(pillars.people);

  if (!carbon && !biodiversity && !people) return undefined;

  return {
    ...(carbon ? { carbon } : {}),
    ...(biodiversity ? { biodiversity } : {}),
    ...(people ? { people } : {}),
  };
};
